// jayesstudio-product-card-a11y.js
//
// JSR-161 (parent JSR-97) — WCAG 2.1.1 (Keyboard) for product-card galleries.
//
// On the PLP and every product-card grid, each card's image gallery is a
// <slideshow-component> whose scroll container (<slideshow-slides ref="scroller">)
// is rendered by the SHARED core snippets/slideshow.liquid as `tabindex="-1"` and
// styled `overflow-x: scroll; scroll-snap-type: x mandatory`. That makes it a real
// horizontally-scrollable region with NO keyboard path: card-gallery.liquid only
// renders the prev/next arrow buttons when `product.media.size > 2 AND there is a
// generic (non-variant) image`, and the core slideshow.js has no keydown handler.
// So for products with <=2 media (or only variant images) the gallery is mouse/
// touch-pannable but keyboard users cannot reach or operate it -> axe
// `scrollable-region-focusable` / 2.1.1 fail.
//
// This is an ADDITIVE progressive enhancement scoped ONLY to product-card galleries
// (`.card-gallery slideshow-component`). It does NOT touch the shared slideshow
// snippet/JS (which also power the hero + PDP slideshows, where arrows always
// render and which install-core would clobber). For each card scroller that is
// actually scrollable it:
//   1. promotes the scroller from tabindex=-1 to tabindex=0 and gives it
//      role="group" + an aria-label, and
//   2. adds ArrowLeft/ArrowRight/Home/End keydown handling that delegates to the
//      component's existing public previous()/next()/select() methods — reusing the
//      same scroll-snap navigation as the arrow buttons (no reimplementation).
//
// When a merchant turns the `product_card_carousel` theme setting OFF, the
// slideshow renders with disabled="true" / overflow:hidden, the scroller is not
// scrollable, and this behavior is a no-op (no stray tabindex/role) — so the fix
// tracks the existing customizer surface rather than adding a parallel toggle.

(() => {
  const ENHANCED = 'data-js-keyboard-enhanced';

  // Localized aria-label template, passed from scripts.liquid via the script tag's
  // dataset so the copy stays translatable in locales/en.default.json. Falls back
  // to an English default if (for any reason) the attribute is absent.
  const currentScript = document.currentScript;
  const ARIA_TEMPLATE =
    (currentScript && currentScript.dataset && currentScript.dataset.ariaLabel) ||
    '%{product_title} images, use the left and right arrow keys to browse';

  /**
   * @param {HTMLElement} component - a <slideshow-component> custom element
   * @returns {HTMLElement|null} the scroll container
   */
  const getScroller = (component) =>
    component.querySelector('slideshow-slides[ref="scroller"]') ||
    component.querySelector('slideshow-slides');

  /** Is this gallery actually a scrollable, non-disabled carousel? */
  const isScrollable = (component, scroller) => {
    if (!scroller) return false;
    // Mirror the core slideshow.js `disabled` getter: disabled="true" OR a
    // mobile-disabled component below the large breakpoint.
    if (component.getAttribute('disabled') === 'true') return false;
    // Real horizontal overflow means there is more than one slide to scroll to.
    return scroller.scrollWidth > scroller.clientWidth + 1;
  };

  const ariaLabelFor = (component) => {
    // card-gallery.liquid renders the product title on the gallery link
    // (`<a ref="cardGalleryLink" aria-label="{{ product.title }}">`); reuse it so
    // the announced name matches the card without a second data attribute.
    const link =
      component.closest('.card-gallery')?.querySelector('[ref="cardGalleryLink"]') ||
      component.closest('.card-gallery')?.querySelector('a.contents');
    const title = (link && link.getAttribute('aria-label')) || '';
    return ARIA_TEMPLATE.replace('%{product_title}', title).trim();
  };

  const onKeydown = (event) => {
    const scroller = event.currentTarget;
    const component = scroller.closest('slideshow-component');
    if (!component) return;

    // If the merchant disabled the carousel after enhancement (or it stopped being
    // scrollable), do nothing.
    if (!isScrollable(component, scroller)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const options = reduceMotion ? { animate: false } : undefined;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        if (typeof component.next === 'function') component.next(undefined, options);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (typeof component.previous === 'function') component.previous(undefined, options);
        break;
      case 'Home':
        event.preventDefault();
        if (typeof component.select === 'function') component.select(0, undefined, options);
        break;
      case 'End': {
        event.preventDefault();
        const last = (component.slides?.length || 1) - 1;
        if (typeof component.select === 'function') component.select(last, undefined, options);
        break;
      }
      default:
        break;
    }
  };

  const enhance = (component) => {
    const scroller = getScroller(component);
    if (!scroller) return;
    if (scroller.getAttribute(ENHANCED) === 'true') return;
    if (!isScrollable(component, scroller)) return;

    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'group');
    scroller.setAttribute('aria-label', ariaLabelFor(component));
    scroller.setAttribute(ENHANCED, 'true');
    scroller.addEventListener('keydown', onKeydown);
  };

  const enhanceAll = (root = document) => {
    root.querySelectorAll('.card-gallery slideshow-component').forEach(enhance);
  };

  const run = () => enhanceAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  // Re-enhance galleries that arrive after first paint (PLP infinite scroll /
  // Section Rendering API swaps, quick-add, etc.). Cheap: enhance() is idempotent
  // and skips already-enhanced or non-scrollable scrollers.
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('.card-gallery slideshow-component')) enhance(node);
          if (node.querySelectorAll) enhanceAll(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
